<template>
  <main class="mx-auto">
    <template v-if="isLoading">
      <div class="flex flex-col gap-6 py-36 px-5 container mx-auto">
        <UtilsSkeletonLoading class="w-full lg:w-3/4 h-36" />
        <UtilsSkeletonLoading class="w-[90%] lg:w-1/2 h-24" />
      </div>
    </template>
    <CommonBanner
      v-else
      :data="expertiseDetail"
      :in-detail="true"
      :light-theme="true"
      text-to="Expertises"
      url="/expertises"
      :style="{
        'background-image': `url(${bgImage})`
      }"
      :half-width="true"
    />

    <section class="container mx-auto py-6 md:py-10">
      <h2 class="section-heading mb-12">
        {{ expertiseDetail?.attributes?.headline || '' }}
      </h2>
      <p
        class="text-sm sm:text-base text-bw-900 w-full md:w-3/4 mb-5 whitespace-pre-line"
      >
        {{ expertiseDetail?.attributes?.challenge || '' }}
      </p>
    </section>

    <section class="container mx-auto py-6 md:py-10">
      <h2
        class="text-center sm:text-left section-heading sm:w-full mx-auto sm:mx-0"
      >
        {{ expertiseDetail?.attributes?.headline_service }}
      </h2>
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7 md:gap-y-10 mt-8 md:mt-10"
      >
        <template v-if="isLoading">
          <div v-for="i in 2" :key="i" class="flex">
            <UtilsSkeletonLoading class="w-6 h-7" />
            <div class="ml-4 flex-1">
              <UtilsSkeletonLoading class="w-full mb-2 h-7" />
              <UtilsSkeletonLoading class="w-full h-24" />
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="content in expertiseDetail.attributes?.contents"
            :key="content.id"
            class="flex items-start"
          >
            <div class="icon w-10">
              <NuxtImg
                format="webp"
                srcset=""
                loading="lazy"
                :src="content?.icon?.data?.attributes?.url || '/'"
                class="w-full h-full"
                :alt="content.title"
                :title="content.title"
              />
            </div>
            <div class="ml-4 flex-1 text-bw-900">
              <h3 class="font-medium text-lg mb-2">{{ content?.title }}</h3>
              <p class="text-base">
                {{ content?.description }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </section>

    <section
      class="container mx-auto py-6 md:py-10 text-bw-900 content"
      v-html="expertiseDetail.attributes?.content"
    ></section>

    <div class="container mx-auto">
      <WorkCaseStudiesList
        :header-text="
          expertiseDetail.attributes?.headline_work ||
          `Success Stories from Our ${expertiseDetail.attributes?.title} Projects`
        "
        :filter-expertise="expertiseDetail.attributes?.title"
      />

      <CommonFAQ
        :title="
          expertiseDetail?.attributes?.headline_faq ||
          `Frequently Asked Questions About ${expertiseDetail?.attributes?.title}`
        "
        type="expertise"
        :component-id="expertiseDetail?.id"
      />

      <WorkArticlesList
        :header-text="
          expertiseDetail.attributes?.headline_article ||
          `Expert Insights and Latest Trends in ${expertiseDetail.attributes?.title}`
        "
        :filter-expertise="expertiseDetail.attributes.title"
      />
    </div>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo

const route = useRoute()
const isLoading = ref(true)

const expertiseDetail = ref([])

const getExpertiseDetail = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/expertices/${route.params.slug}`,
    {
      query: {
        'fields[0]': 'id',
        'fields[1]': 'content',
        'fields[2]': 'title',
        'fields[3]': 'content',
        'fields[4]': 'challenge',
        'fields[5]': 'headline',
        populate:
          'banner.image,banner.background_image,contents.icon,seo.image,headline'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Industry does not exist`
    })
  }

  return data.value
}

const expertise = await getExpertiseDetail()
expertiseDetail.value = expertise.data

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: expertiseDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: expertiseDetail?.value?.attributes?.url || 'suitmedia.com',
  description:
    expertiseDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    expertiseDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    expertiseDetail?.value?.attributes?.banner?.background_image?.data
      ?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title: expertiseDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: expertiseDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    expertiseDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    expertiseDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    expertiseDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    expertiseDetail?.value?.attributes?.banner?.background_image?.data
      ?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: expertiseDetail?.value?.attributes?.seo?.open_graph_type,
  twitterTitle:
    expertiseDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    expertiseDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    expertiseDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    expertiseDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})

const bgImage = computed(() => {
  return expertiseDetail.value?.attributes?.banner?.background_image?.data
    ?.attributes?.url
})
</script>

<style lang="scss" scoped>
.icon {
  :deep(.nuxt-icon) {
    svg {
      width: 36px;
      height: 36px;
    }
  }
}

:deep(.content) {
  h1,
  h2 {
    text-align: center;
    font-weight: 500 !important;
    font-size: 22px;
    line-height: normal;
    margin: 0 auto 2rem;
    width: 100%;
    color: #353535;

    @media screen and (min-width: 768px) {
      text-align: left;
      font-size: 40px;
      line-height: 50px;
      margin: 0 0 2rem;
      width: 66%;
    }
  }

  h3 {
    font-size: 14px;
    font-weight: 500 !important;
    line-height: 22px;
    color: #353535;
    margin-top: 24px;
    margin-bottom: 24px;

    @media screen and (min-width: 768px) {
      font-size: 32px;
      line-height: 40px;
    }
  }

  h4 {
    font-size: 14px;
    font-weight: 500 !important;
    line-height: 22px;
    color: #353535;
    margin-top: 24px;
    margin-bottom: 24px;

    @media screen and (min-width: 768px) {
      font-size: 24px;
      line-height: 32px;
    }
  }

  h5 {
    font-size: 18px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 26px;
    font-weight: 500 !important;
    color: #353535;
  }

  h6 {
    font-size: 16px;
    margin-top: 24px;
    margin-bottom: 24px;
    line-height: 24px;
    font-weight: 500 !important;
    color: #353535;
  }

  p {
    font-size: 14px;
    line-height: 22px;
    width: 100%;
    margin-bottom: 24px;
    color: #353535;

    @media screen and (min-width: 768px) {
      font-size: 16px;
      line-height: 24px;
      width: 66%;
    }
  }

  ol {
    list-style: decimal;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
    }
  }

  ul {
    list-style-type: disc;
    margin-left: 32px;
    margin-bottom: 20px;

    @media screen and (min-width: 768px) {
      width: 60%;
    }

    li {
      margin-bottom: 1rem;
    }
  }

  img {
    margin-bottom: 6px;
    max-width: 100%;
    width: 100%;

    @media screen and (min-width: 768px) {
      width: 66%;
    }
  }
}
</style>
